import React from "react";
import { DateTime } from "luxon";

import {
  Constraint,
  Identifiable,
  OptimizedGantt,
  Project,
  ProjectElementType,
} from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { Context as GanttContext } from "../../context";
import { isList, isMilestone, isTask } from "../elementsOverlay/element/utils";
import {
  constraintToEl,
  Context,
  generateArrow,
  getDirection,
  getPath,
  projectElementToEvent,
  projectElementToList,
  projectElementToMilestone,
  projectElementToTask,
  useConstraints,
  useDrag,
} from "./utils";
import { Props } from "./types";
import styles from "./styles.scss";

type ProjectElement = OptimizedGantt[number];

function ConstraintsOverlay({
  width,
  constraints,
  dates,
  graphRef,
  children,
  project,
}: Props) {
  const userTimezone = DateTime.local().zoneName;
  const { ...config } = React.useContext(GanttContext);
  const { pushView } = useModal();
  const [state, setState] = React.useState<{
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    trackedX: number;
    trackedY: number;
    origin: ProjectElement | null;
    target: ProjectElement | null;
    project: Identifiable<Project> | null;
    originDateType: Constraint["originDateType"];
    targetDateType: Constraint["originDateType"];
  }>({
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    trackedX: 0,
    trackedY: 0,
    origin: null,
    target: null,
    project: null,
    originDateType: "START",
    targetDateType: "START",
  });
  const [hoveredConstraint, setHoveredConstraint] =
    React.useState<Identifiable<Constraint> | null>(null);
  const correctPos = (x: number, y: number) => {
    if (graphRef.current) {
      const rect = graphRef.current.getBoundingClientRect();

      return { x: x - rect.left, y: y - rect.top };
    }

    return { x, y };
  };
  const startPos = correctPos(state.startX, state.startY);
  const trackedPos = correctPos(state.trackedX, state.trackedY);
  const getHeight = () => {
    if (graphRef.current) {
      const rect = graphRef.current.getBoundingClientRect();

      return rect.height;
    }

    return 0;
  };
  const { origin, target, originDateType, targetDateType } = state;
  const constraint: Constraint & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  } = {
    ...(target
      ? {
          targetEntityUuid: target.uuid,
          targetStartDate: target.startDate,
          targetEndDate: target.endDate,
          targetIsDone: false,
        }
      : {
          targetEntityUuid: "",
          targetStartDate: "",
          targetEndDate: "",
          targetIsDone: false,
        }),
    targetEntityType: target
      ? isTask(target)
        ? "TASK"
        : isMilestone(target)
          ? "MILESTONE"
          : isList(target)
            ? "ACTIVITY"
            : "TASK"
      : "TASK",
    targetDateType,
    ...(origin
      ? {
          originEntityUuid: origin.uuid,
          originStartDate: origin.startDate,
          originEndDate: origin.endDate,
          originIsDone: false,
        }
      : {
          originEntityUuid: "",
          originStartDate: "",
          originEndDate: "",
          originIsDone: false,
        }),
    originEntityType: origin
      ? isTask(origin)
        ? "TASK"
        : isMilestone(origin)
          ? "MILESTONE"
          : isList(origin)
            ? "ACTIVITY"
            : "TASK"
      : "TASK",
    originDateType,
    constraintType: "EXACTLY",
    offsetDays: 0,
    timeUnit: "DAY",
  };
  const minDate = dates[0] || DateTime.now().setZone(userTimezone);
  const constraintPath =
    !!origin && !!target
      ? getPath(constraint, minDate, config, userTimezone, 50)
      : [
          [startPos.x, startPos.y],
          [trackedPos.x, trackedPos.y],
        ];
  const constraintDirection = getDirection(constraintPath);

  useDrag(
    (x, y) =>
      !!origin &&
      setState((s) =>
        target
          ? { ...s, lastX: x, lastY: y }
          : { ...s, lastX: x, lastY: y, trackedX: x, trackedY: y },
      ),
    () => {
      if (!!origin && !!target && !!state.project) {
        pushView({
          key: UseCases.constrainElements,
          data: {
            constraint,
            project: state.project,
            target:
              target.type === ProjectElementType.LIST
                ? projectElementToList(target)
                : target.type === ProjectElementType.TASK
                  ? projectElementToTask(target)
                  : target.type === ProjectElementType.MILESTONE
                    ? projectElementToMilestone(target)
                    : target.type === ProjectElementType.EVENT
                      ? projectElementToEvent(target)
                      : projectElementToTask({
                          ...target,
                          type: ProjectElementType.TASK,
                        } as any),
            origin:
              origin.type === ProjectElementType.LIST
                ? projectElementToList(origin)
                : origin.type === ProjectElementType.TASK
                  ? projectElementToTask(origin)
                  : origin.type === ProjectElementType.MILESTONE
                    ? projectElementToMilestone(origin)
                    : origin.type === ProjectElementType.EVENT
                      ? projectElementToEvent(origin)
                      : projectElementToTask({
                          ...origin,
                          type: ProjectElementType.TASK,
                        } as any),
          },
        });
      }

      !!origin && setState((s) => ({ ...s, origin: null, target: null }));
    },
    [origin, target, originDateType, targetDateType],
  );

  return (
    <Context.Provider
      value={{
        x: state.lastX,
        y: state.lastY,
        isDragging: !!origin,
        origin,
        originDateType,
        target,
        targetDateType,
        startDrag: (data) =>
          setState((s) => ({
            ...s,
            origin: data.element,
            project: data.project,
            originDateType: data.dateType,
            startX: data.x,
            startY: data.y,
            lastX: data.x,
            lastY: data.y,
            trackedX: data.x,
            trackedY: data.y,
          })),
        dragInElement: (data) =>
          setState((s) => ({
            ...s,
            target: data.element,
            targetDateType: data.dateType,
            trackedX: data.snapPoint.x,
            trackedY: data.snapPoint.y,
          })),
        dragOutElement: (data) =>
          setState((s) => ({
            ...s,
            target:
              s.target && data.element.uuid === s.target.uuid ? null : s.target,
          })),
      }}
    >
      <div className={styles.constraintsOverlay}>
        <svg viewBox={`0 0 ${width} ${getHeight()}`}>
          {constraints
            .filter(
              (constraint) =>
                typeof constraint.originEntityUuid !== "undefined" &&
                typeof constraint.targetEntityUuid !== "undefined",
            )
            .map((constraint) => {
              const path = getPath(constraint, minDate, config, userTimezone);
              const direction = getDirection(path);
              const isHovered =
                hoveredConstraint && hoveredConstraint.uuid === constraint.uuid;

              return (
                <g key={constraint.uuid}>
                  <polyline
                    stroke="transparent"
                    fill="none"
                    cursor="pointer"
                    strokeWidth={12}
                    points={path.map((point) => point.join(",")).join(" ")}
                    onMouseEnter={() => setHoveredConstraint(constraint)}
                    onMouseLeave={() => setHoveredConstraint(null)}
                    onClick={() =>
                      isHovered &&
                      pushView({
                        key: UseCases.editConstraint,
                        data: {
                          constraint,
                          origin: constraintToEl(constraint, "origin"),
                          target: constraintToEl(constraint, "target"),
                          project,
                        },
                      })
                    }
                  />
                  <polyline
                    stroke={isHovered ? "#FFA439" : "#DBDBDB"}
                    fill="none"
                    cursor="pointer"
                    strokeWidth={2}
                    points={path.map((point) => point.join(",")).join(" ")}
                    onMouseEnter={() => setHoveredConstraint(constraint)}
                    onMouseLeave={() => setHoveredConstraint(null)}
                    onClick={() =>
                      isHovered &&
                      pushView({
                        key: UseCases.editConstraint,
                        data: {
                          constraint,
                          origin: constraintToEl(constraint, "origin"),
                          target: constraintToEl(constraint, "target"),
                          project,
                        },
                      })
                    }
                  />
                  {direction && (
                    <path
                      fill={isHovered ? "#FFA439" : "#DBDBDB"}
                      stroke={isHovered ? "#FFA439" : "#DBDBDB"}
                      strokeWidth={2}
                      d={generateArrow(path, direction)}
                    />
                  )}
                </g>
              );
            })}
          {!!origin && (
            <g>
              <polyline
                fill="none"
                stroke="#FFA439"
                strokeWidth={2}
                points={constraintPath
                  .map((point) => point.join(","))
                  .join(" ")}
              />
              {!!origin && !!target && (
                <path
                  fill="#FFA439"
                  stroke="#FFA439"
                  strokeWidth={2}
                  d={generateArrow(constraintPath, constraintDirection)}
                />
              )}
            </g>
          )}
        </svg>
      </div>
      {children}
    </Context.Provider>
  );
}

export { ConstraintsOverlay, useConstraints };
